<script lang="ts">
import {defineComponent, type PropType} from 'vue';
import type {ViewBoxController} from './viewbox_controller.ts';
import {setupViewBoxPanZoom, dollyZoomToSelector, type ViewBoxPanZoomHandle} from './viewbox_panzoom.ts';
import {bakePourMasks} from './bake_pour_masks.ts';
import {refreshDiffLayers} from '../AllSpiceDiff/diff-layer-utils.ts';
import {useDiffToggleStore} from '../../allspice_app/stores/diffToggle.ts';
import {mapStores} from 'pinia';
import {getDocumentDiffData} from '../../allspice_app/api/crysknife.ts';
import {collectDiffOverlaySuppressedTargets, type DiffOverlaySuppressedTarget} from '../../allspice_app/components/diff-list/diff-grouping.ts';
import {scopeDiffJsonToPage} from '../../allspice_app/components/diff-list/diff-list-state.ts';
import {toggleHighlightElement} from '../document_highlight.ts';
import {onNextFrame, waitForRender} from '../../domutils.ts';
import EcadInspectorFrame from '../EcadInspectorFrame.vue';
import {applyColorProfile} from '../pcb_colors.ts';
import AllSpiceSnippetReference from '../AllSpiceSnippetReference.vue';
import {afterSnippetClickThrough, fitViewCoordinates} from './view_coordinates_zoom.ts';

import {AllSpiceJsonLoader, SCHEMA_TYPES} from '../../features/allspicejson.ts';
import {buildDiffTree, normalizeDiffTreeData, type DiffJson} from '../diff_json.ts';
import {resolveInspectorEntity} from '../inspector_entity_lookup.ts';
import type {InspectorDiffTreeState} from '../inspector_diff_tree.ts';
import {InspectorContext, headOrBase, type TreeNode} from '../inspector_tree.ts';
import {svgReapplyFilter} from '../AllSpiceDiff/AllSpiceDiffViewStyle.ts';
import {hideElem, showElem} from '../../utils/dom.ts';

import type {ProjectData} from '../../allspice_app/types/project-data.ts';
import type {FetchRetryer} from '../../fetchretryer.ts';
import type {Container} from '../../container.ts';
import type {InspectorDataLoadEventDetails} from './types.ts';
import type {Layer} from '../../allspice_app/stores/layers.ts';

const {assetUrlPrefix} = window.config;

export type EcadDocumentData = {
  id: string,
  isLoading: boolean,
  isLoadError: boolean,
  isHeadLoaded: boolean,
  isDiffFromLoaded: boolean,
  isDiffToLoaded: boolean,
  isDiffLoadError: boolean,
  diffFromSvg: string,
  diffToSvg: string,
  showErrorDetails: boolean,
  errorDetailsText: string,
  panZoomHelpText: string,
  panZoomHelpClassNames: string,
  clientX: number,
  clientY: number,
  panZoom: ViewBoxController | undefined,
  panZoomHandle: ViewBoxPanZoomHandle | null,
  currentNet: string,
  isLoadingIndicatorEnabled: boolean,
  referenceElement: HTMLElement | null,
  foldedModel: boolean,
  allLoaded: boolean,
  loadedProjectData: ProjectData | null,
  isPcb: boolean,
  foldObserver: MutationObserver | null,
  suppressedNonVisualDiffTargets: DiffOverlaySuppressedTarget[] | null,
  suppressedNonVisualDiffTargetsPromise: Promise<DiffOverlaySuppressedTarget[]> | null,
};

export default defineComponent({
  components: {
    EcadInspectorFrame,
    'allspice-snippet-reference': AllSpiceSnippetReference,
  },
  inject: {
    untypedContainer: {from: 'container'},
    inspectorDiffTreeState: {from: 'inspectorDiffTreeState', default: undefined},
  },
  props: {
    // A single document (schematic or component) from the full container.
    baseDocument: {
      type: Object as PropType<Record<string, any>>,
      required: false,
      default: undefined,
    },
    // A single document (schematic or component) from the full container.
    headDocument: {
      type: Object as PropType<Record<string, any>>,
      required: false,
      default: undefined,
    },
    nets: {
      type: Object, // Record<string, Net>
      required: false,
      default: () => ({}),
    },
    areDocumentsLoadedPromise: {
      type: Promise as PropType<Promise<void>>,
      required: true,
    },
    headSvg: {
      type: String,
      required: false,
      default: undefined,
    },
    isDiffLoadedPromise: {
      type: Promise as PropType<Promise<[FetchRetryer, FetchRetryer]>>,
      required: true,
    },
    // True for a diff between two commits, false to view a file (uses head params).
    isDiff: {
      type: Boolean,
      required: false,
      default: true,
    },
    baseRepoOwner: {
      type: String,
      required: false,
      default: '',
    },
    baseRepoName: {
      type: String,
      required: false,
      default: '',
    },
    baseCommit: {
      type: String,
      required: false,
      default: '',
    },
    baseTreePath: {
      type: String,
      required: false,
      default: '',
    },
    headRepoOwner: {
      type: String,
      required: true,
      default: '',
    },
    headRepoName: {
      type: String,
      required: true,
      default: '',
    },
    headCommit: {
      type: String,
      required: true,
      default: '',
    },
    headTreePath: {
      type: String,
      required: true,
      default: '',
    },
    docId: {
      type: String,
      required: true,
      default: '',
    },
    // Value is either three dots or two dots.
    diffType: {
      type: String,
      required: false,
      default: '...',
    },
    // Whether panning and zooming is enabled.
    isPanZoomEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    // Alt text for accessibility.
    alt: {
      type: String,
      required: false,
      default: '',
    },
    // The SVG class(es): as-snippet, as-thumbnail.
    svgClassNames: {
      type: String,
      required: false,
      default: '',
    },
    dataAsViewCoords: {
      type: String,
      required: false,
      default: '',
    },
    // Width-to-height aspect ratio.
    dataAsAspectRatio: {
      type: String,
      required: false,
      default: '',
    },
    // When true and loading the image errors, displays a default image.
    displayDefaultOnError: {
      type: Boolean,
      required: false,
      default: true,
    },
    colorProfile: {
      type: Object,
      required: false,
      default: null,
    },
    deferredRender: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialSnippetClickthrough: {
      type: Boolean,
      required: false,
      default: false,
    },
    folded: {
      type: Boolean,
      required: false,
      default: false,
    },
    projectData: {
      type: Object as PropType<ProjectData>,
      required: false,
      default: null,
    },
  },

  data(): EcadDocumentData {
    return {
      id: '',
      isLoading: true,
      isLoadError: false,
      isHeadLoaded: false,
      isDiffFromLoaded: false,
      isDiffToLoaded: false,
      isDiffLoadError: false,
      diffFromSvg: '',
      diffToSvg: '',
      showErrorDetails: false,
      errorDetailsText: '',
      panZoomHelpText: '',
      panZoomHelpClassNames: '',
      clientX: NaN,
      clientY: NaN,
      panZoom: undefined,
      panZoomHandle: null,
      currentNet: '',
      isLoadingIndicatorEnabled: false,
      referenceElement: null,
      foldedModel: this.folded,
      allLoaded: false,
      loadedProjectData: null,
      isPcb: false,
      foldObserver: null,
      suppressedNonVisualDiffTargets: null,
      suppressedNonVisualDiffTargetsPromise: null,
    };
  },

  computed: {
    ...mapStores(useDiffToggleStore),
    container(): Container {
      return this.untypedContainer as unknown as Container;
    },
    hideNonVisualDiffOverlayUntilReady() {
      return this.isDiff && !this.isPcb && Boolean(this.baseTreePath) && Boolean(this.headTreePath) && this.suppressedNonVisualDiffTargets === null;
    },
    svgContainerClassNames() {
      // This should match setupSnippetContainer().
      const svgClasses = (this.svgClassNames || '').split(' ');
      const arr = [];
      if (svgClasses.includes('as-snippet')) {
        arr.push('as-snippet-container');
      }
      if (svgClasses.includes('as-thumbnail')) {
        arr.push('as-thumbnail-container');
      }
      return arr;
    },
    failedImageUrl() {
      return `${assetUrlPrefix}/img/failed.svg`;
    },
    isDiffLoading() {
      return this.isDiff && (!this.isDiffFromLoaded || !this.isDiffToLoaded) &&
        !this.isDiffLoadError;
    },
  },

  watch: {
    headSvg() {
      // The <svg> node is swapped (v-html) when diff layers / a variant load: re-wire handlers,
      // re-bake pour masks, and rebind the controller to the new node (after the DOM updates).
      this.setupEventHandlers();
      this.$nextTick(() => {
        const svg = (this.$refs.headSvg as HTMLElement | undefined)?.querySelector('svg');
        if (!svg) return;
        if (this.isPcb) bakePourMasks(svg);
        this.panZoom?.rebind(svg);
      });
    },
  },

  created() {
    if (!this.headSvg) {
      console.warn('headSvg is empty');
    }
    const headTreePathLower = this.headTreePath.toLowerCase();
    this.isPcb = headTreePathLower.endsWith('.pcbdoc') || headTreePathLower.endsWith('.cspcbdoc') || headTreePathLower.endsWith('.kicad_pcb') || headTreePathLower.endsWith('.brd');

    // Wait for both documents and project data before setup.
    const projectDataPromise = this.loadProjectData();

    // eslint-disable-next-line github/no-then
    Promise.all([this.areDocumentsLoadedPromise, projectDataPromise]).then(() => {
      this.setupEventHandlers();
    });

    if (!this.isDiff) return;

    // eslint-disable-next-line github/no-then
    this.isDiffLoadedPromise.then(async ([fromFetchRetryer, toFetchRetryer]: [FetchRetryer, FetchRetryer]) => {
      if (!fromFetchRetryer && !toFetchRetryer) {
        this.onDiffFromLoad();
        this.onDiffToLoad();
        return;
      }
      // Need to clone since this is a shared FetchRetryer, and calling `text()` consumes it.
      const diffFromPromise = fromFetchRetryer.response?.clone().text() ?? Promise.resolve('');
      const diffToPromise = toFetchRetryer.response?.clone().text() ?? Promise.resolve('');
      const [diffFromSvg, diffToSvg] = await Promise.all([diffFromPromise, diffToPromise]);
      this.diffFromSvg = diffFromSvg;
      this.diffToSvg = diffToSvg;
      this.onDiffFromLoad();
      this.onDiffToLoad();
    // eslint-disable-next-line github/no-then
    }).catch((error) => {
      this.isDiffLoadError = true;
      this.displayUnknownError(error);
    });
    if (!this.baseTreePath) {
      this.onDiffFromLoad();
    }
    if (!this.headTreePath) {
      this.onDiffToLoad();
    }
  },
  mounted() {
    document.addEventListener('allspice-snippet-click-thru', this.setupSnippetReference);
    this.id = Math.random().toString(16).slice(2);

    if (this.isDiff) {
      this.foldObserver = new MutationObserver((mutationList) => {
        for (const mutation of mutationList) {
          if (mutation.attributeName === 'data-folded' && (mutation.target as Element).getAttribute('data-folded') === 'false') {
            this.onUnfold();
          }

          // If data-mounted is changed, attempt to setup PanZoom
          if (mutation.attributeName === 'data-mounted' && (mutation.target as Element).getAttribute('data-mounted') === 'true') {
            this.setupPanZoom();
          }
        }
      });
      this.foldObserver.observe(this.$el.closest('.diff-file-box'), {attributes: true, attributeFilter: ['data-folded', 'data-mounted']});
    }

    // Head file preview must wait for mount: the callback relies on this.$refs.
    if (this.headSvg && !this.deferredRender) {
      this.onHeadSvgLoad();
    } else if (!this.deferredRender) {
      console.warn('this.headSvg not loaded on mount (and render not deferred)');
    }
  },

  unmounted() {
    this.removeGlobalEventHandlers();
    document.removeEventListener('allspice-snippet-click-thru', this.setupSnippetReference);
    if (this.foldObserver) {
      this.foldObserver.disconnect();
    }
    this.panZoomHandle?.destroy();
  },

  methods: {
    /**
     * Load project data for variant mappings
     * @returns {Promise<void>}
     */
    async loadProjectData() {
      // Prefer the prop over loading.
      if (this.projectData) {
        this.loadedProjectData = this.projectData;
        return;
      }

      try {
        const prjUrl = this.isDiff ?
          AllSpiceJsonLoader.buildPrjRangeUrl(this.container, this.headRepoOwner, this.headRepoName, this.baseCommit, this.headCommit) :
          AllSpiceJsonLoader.buildPrjUrl(this.container, this.headRepoOwner, this.headRepoName, this.headCommit, this.headTreePath);

        if (prjUrl) {
          const results = await AllSpiceJsonLoader.load<ProjectData>(this.container, prjUrl, null);
          this.loadedProjectData = results.headJson;
        }
      } catch (error) {
        console.warn('Failed to load project data:', error);
      }
    },

    // There was an error loading the head file
    loadErrorCallback() {
      this.displayLoadError();
      // TODO - Is this function even called anywhere? Not just the preview, but the loadErrorCallback
      // this.headLoadPreview.cancel();
      this.isLoading = false;
      this.isLoadError = true;
    },

    diffLoadErrorCallback() {
      this.isDiffLoadError = true;
      this.displayDiffResponseError();
    },
    onHeadSvgError(fetchRetryer: FetchRetryer | undefined) {
      if (fetchRetryer?.response) {
        console.error(fetchRetryer.response);
      }
      this.displayLoadError();
      // TODO - Is this function even called anywhere? Not just the preview, but the onHeadSvgError
      // this.headLoadPreview.cancel();
      this.isLoading = false;
      this.isLoadError = true;
    },
    displayLoadError() {
      console.error(`Head file could not load.`);
      this.errorDetailsText = `Error: Failed to load file from server. Please contact maintainers@allspice.io if this issue persists.`;
      this.showErrorDetails = true;
      this.isLoading = false;     // Disable loader
      this.isLoadError = true;
    },

    /**
     * @param {Response | undefined} resp
     */
    displayDiffResponseError(resp?: Response) {
      console.error(`Diff resp not found. Status code: ${resp?.status}`);
      if (resp) {
        this.errorDetailsText = `Error: Failed to receive diff from server. Status code: ${resp.status}. Please contact maintainers@allspice.io if this issue persists.`;
      } else {
        this.errorDetailsText = `Error: Failed to receive diff from server. Please contact maintainers@allspice.io if this issue persists.`;
      }
      this.showErrorDetails = true;
    },

    displayUnknownError(err: unknown) {
      console.error(err);
      this.errorDetailsText = 'Error: Failed to load diff. Please contact maintainers@allspice.io if this issue persists.';
      this.showErrorDetails = true;
    },

    onHeadSvgLoad() {
      // Guard against the load event firing more than once.
      if (this.isHeadLoaded) {
        return;
      }
      this.isLoading = false; // Disable loader
      const svg = (this.$refs.headSvg as HTMLElement).querySelector('svg');
      if (svg === null) {
        console.error("Couldn't find working SVG top-level element");
        return;
      }

      this.isHeadLoaded = true;

      // Notify that the preview has loaded, only after the SVG is in the DOM.
      const loadedEvent = new CustomEvent('previewload', {
        bubbles: true,
        detail: {
          headTreePath: this.headTreePath,
        },
      });
      svg.dispatchEvent(loadedEvent);

      this.checkIfAllLoaded();
    },

    checkIfAllLoaded() {
      // Wait for the head and any diffs to finish loading.
      if (!this.isHeadLoaded) return;
      if (this.isDiffLoading) return;

      this.onAllLoad();
    },

    /**
     * This should only ever get called once when all SVGs are loaded.
     */
    async onAllLoad() {
      const svg = (this.$refs.headSvg as HTMLElement).querySelector('svg');

      if (this.colorProfile !== null) {
        applyColorProfile(svg, this.colorProfile);

        // applyColorProfile can write CSS, so wait for it to render.
        await waitForRender();
      }

      if (this.isDiff) {
        // reapply the filter if needed
        svgReapplyFilter(svg, this.docId);
      }

      if (this.isPcb && svg) {
        bakePourMasks(svg);
      }

      if (!this.foldedModel) {
        await this.completeSetup();
      }

      this.setupEventHandlers();

      this.allLoaded = true;
    },

    /**
     * Complete setup once all svgs loaded, concatenated
     */
    async completeSetup() {
      this.setupPanZoom();

      // Let setupPanZoom's ResizeObserver center the container before snippet
      // reference setup, which needs accurate element dimensions.
      await waitForRender();
      this.setupSnippetReference();
      this.enableDiffToggle();
    },

    /**
     * Called when a diff view is unfolded
     */
    onUnfold() {
      if (this.allLoaded && this.foldedModel) {
        // Loaded already, so finish setup now.
        this.completeSetup();
      }
      this.foldedModel = false;
    },

    /**
     * Once layers are loaded, enable the diff toggle buttons
     */
    enableDiffToggle() {
      if (this.isDiff) {
        this.diffToggleStore.enable();
        // Apply the current toggle state (possibly restored from persisted config)
        // to the freshly loaded SVG.
        refreshDiffLayers(this.$el.closest('.diff-file-box') ?? document);
      }
    },

    /**
     * Determine whether select region mode is enabled
     * @return {boolean}
     */
    selectRegionEnabled() {
      const parent = this.$el.closest('.diff-file-box.has-selectable-region') ||
        this.$el.closest('.non-diff-file-content.has-selectable-region');

      if (parent) {
        return parent.classList.contains('select-region-mode');
      }
      return false;
    },

    /**
     * Toggle the current net on/off
     * @param {SVGSVGElement} svg the svg to toggle
     * @param {boolean} toggleOn whether the net is being toggled on or off
     * @param {boolean} closeInspector whether to close inspector when done
     * @returns {Promise<void>}
     */
    toggleNet(svg: SVGSVGElement, toggleOn: boolean, closeInspector: boolean) {
      return new Promise<void>((resolve, reject) => {
        onNextFrame(() => {
          try {
            if (toggleOn) {
              toggleHighlightElement(svg, `[data-net="${CSS.escape(this.currentNet)}"]`, true, 'net', this.isPcb);
              refreshDiffLayers(this.$el.closest('.diff-file-box') ?? document);
            } else if (this.currentNet !== '') {
              // Unhighlight the last net.
              toggleHighlightElement(svg, `[data-net="${CSS.escape(this.currentNet)}"]`, false, 'net', this.isPcb);
              this.currentNet = '';
            }
            if (closeInspector) {
              // TODO: Type properly after EcadInspectorFrame is typed
              (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
            }
            resolve(null);
          } catch (error) {
            reject(error);
          }
        });
      });
    },

    /**
     * Toggle the loading indicator and set flag
     * @param {boolean} isLoading
     */
    setLoadingIndicator(isLoading: boolean) {
      this.isLoadingIndicatorEnabled = isLoading;
      const loadingIndicator = this.findASLoading();
      if (loadingIndicator !== null) {
        if (isLoading) {
          showElem(loadingIndicator);
        } else {
          hideElem(loadingIndicator);
        }
      }
    },

    /**
     * Event handler setup
     */
    setupEventHandlers() {
      const headContents = this.headDocument;
      const baseContents = this.baseDocument;

      const headSvgElem = this.$refs.headSvg as HTMLElement;
      const svg = headSvgElem.querySelector('svg') as SVGSVGElement;

      svg.dispatchEvent(new CustomEvent<InspectorDataLoadEventDetails>('inspectordataload', {
        bubbles: true,
        detail: {
          baseContents,
          headContents,
          layers: headOrBase(headContents, baseContents, 'layers', {}) as Record<number, Layer>,
          presets: headOrBase(headContents, baseContents, 'preset_layers', {}) as Record<string, number[]>,
          // PCB components are stored separately
          pcbComponents: headOrBase(headContents, baseContents, 'component_instances', {}),
          // SCH components stored inline
          schComponents: (headContents !== null) ? headContents : ((baseContents !== null) ? baseContents : {}),
          nets: this.nets,
        },
      }));

      this.hideNonVisualDiffOverlay();

      // Remove event handlers that were setup during a previous call.
      this.removeGlobalEventHandlers();
      document.body.addEventListener('keydown', this.onKeyDown);
      document.body.addEventListener('inspector-hide', this.handleinspectorHide);
      document.body.addEventListener('dolly-zoom-request', this.handleDollyZoomRequest);

      // prevent double-registering event handlers
      headSvgElem.removeEventListener('inspector-click', this.handleInspectorClick);
      headSvgElem.addEventListener('inspector-click', this.handleInspectorClick);

      // prevent double-registering event handlers
      headSvgElem.removeEventListener('inspector-request', this.handleInspectorRequest);
      headSvgElem.addEventListener('inspector-request', this.handleInspectorRequest);
    },

    async hideNonVisualDiffOverlay() {
      try {
        if (!this.isDiff) {
          return;
        }

        const suppressedTargets = await this.loadSuppressedNonVisualDiffTargets();

        // setupEventHandlers can rerun as the SVG changes, so clear previous
        // suppression markers before reapplying against the current DOM.
        for (const hiddenElement of this.$el.querySelectorAll('[data-hidden-nonvisual-diff="true"]') as NodeListOf<SVGElement>) {
          hiddenElement.style.removeProperty('visibility');
          hiddenElement.removeAttribute('data-hidden-nonvisual-diff');
        }

        for (const {selector} of suppressedTargets) {
          const scopedSelector = `.diff_from ${selector}, .diff_to ${selector}`;
          for (const element of this.$el.querySelectorAll(scopedSelector) as NodeListOf<SVGElement>) {
            element.style.visibility = 'hidden';
            element.setAttribute('data-hidden-nonvisual-diff', 'true');
          }
        }
      } catch (error) {
        console.error(error);
      }
    },

    async loadSuppressedNonVisualDiffTargets() {
      if (this.suppressedNonVisualDiffTargets !== null) {
        return this.suppressedNonVisualDiffTargets;
      }

      if (this.suppressedNonVisualDiffTargetsPromise !== null) {
        return await this.suppressedNonVisualDiffTargetsPromise;
      }

      if (!this.baseTreePath || !this.headTreePath) {
        this.suppressedNonVisualDiffTargets = [];
        return this.suppressedNonVisualDiffTargets;
      }

      this.suppressedNonVisualDiffTargetsPromise = (async () => {
        try {
          const diffData = await getDocumentDiffData({
            repoOwner: this.headRepoOwner,
            repoName: this.headRepoName,
            headCommit: this.headCommit,
            baseCommit: this.baseCommit,
            headDocumentPath: this.headTreePath,
            baseDocumentPath: this.baseTreePath,
          });

          const headContents = this.headDocument ?? null;
          const baseContents = this.baseDocument ?? null;
          const currentDocument = headContents ?? baseContents;
          if (!currentDocument) {
            this.suppressedNonVisualDiffTargets = [];
            return this.suppressedNonVisualDiffTargets;
          }

          const normalizedDiffData = normalizeDiffTreeData(currentDocument, diffData);
          const scopedDiffJson = this.scopeDiffJsonToCurrentDocument(normalizedDiffData.diffJson);
          const schemaType = this.isPcb ? SCHEMA_TYPES.PCB : SCHEMA_TYPES.SINGLE_PAGE_SCHEMATIC;
          const metadataContext = this.buildInspectorMetadataContext(headContents ?? {}, baseContents ?? {});
          const diffTree = buildDiffTree({
            headJson: currentDocument,
            diffJson: scopedDiffJson,
          }, metadataContext, {includeUnchangedEntities: false});

          this.suppressedNonVisualDiffTargets = collectDiffOverlaySuppressedTargets(schemaType, diffTree);
        } catch (error) {
          console.error(error);
          this.suppressedNonVisualDiffTargets = [];
        } finally {
          this.suppressedNonVisualDiffTargetsPromise = null;
        }

        return this.suppressedNonVisualDiffTargets;
      })();

      return await this.suppressedNonVisualDiffTargetsPromise;
    },

    // Symbol doc IDs are a page ID plus a numeric suffix, so try both the full
    // doc ID and the parent page ID when scoping page diffs.
    getScopedDiffPageIdCandidates() {
      const pageIdCandidates = [this.docId];
      const docIdParts = this.docId.split('-');
      const symbolIndex = docIdParts.at(-1);

      if (symbolIndex && /^\d+$/.test(symbolIndex) && docIdParts.length > 1) {
        pageIdCandidates.push(docIdParts.slice(0, -1).join('-'));
      }

      return pageIdCandidates;
    },

    scopeDiffJsonToCurrentDocument(diffJson: DiffJson) {
      for (const pageId of this.getScopedDiffPageIdCandidates()) {
        const scopedDiffJson = scopeDiffJsonToPage(diffJson, pageId);
        const pageNode = scopedDiffJson[`/pages/${pageId}`];
        if (pageNode && 'children' in pageNode && pageNode.children) {
          return pageNode.children;
        }
      }

      return diffJson;
    },

    async handleInspectorClick(e: Event) {
      e.preventDefault();

      const customEvent = e as CustomEvent<{target: Element}>;
      const svg = (this.$refs.headSvg as HTMLElement).querySelector('svg') as SVGSVGElement;

      const inspectorDiffTreeState = this.inspectorDiffTreeState as InspectorDiffTreeState | undefined;
      const prebuiltTree = inspectorDiffTreeState?.findPrebuiltInspectorTreeForElement(customEvent.detail.target) ?? null;

      this.showInspectorForElement(customEvent.detail.target, svg, this.baseDocument, this.headDocument, customEvent, prebuiltTree);
    },

    async handleInspectorRequest(e: Event) {
      const {target, selector, treeNode} = (e as CustomEvent<{target: Element, selector: string, treeNode?: TreeNode | null}>).detail;
      const svg = (this.$refs.headSvg as HTMLElement).querySelector('svg') as SVGSVGElement;

      // pan and zoom to the element before popping up the inspector
      await this.dollyZoom(svg, selector);

      const inspectorDiffTreeState = this.inspectorDiffTreeState as InspectorDiffTreeState | undefined;
      const prebuiltTree = treeNode ?? inspectorDiffTreeState?.findPrebuiltInspectorTreeForElement(target) ?? null;

      this.showInspectorForElement(target, svg, this.baseDocument, this.headDocument, null, prebuiltTree);
    },

    async handleinspectorHide() {
      this.hideInspector();
    },

    buildInspectorMetadataContext(headContents: Record<string, any>, baseContents: Record<string, any>) {
      return new InspectorContext({
        window,
        treePath: this.headTreePath,
        repoOwner: this.headRepoOwner,
        repoName: this.headRepoName,
        commitSHA: this.headCommit,
        layers: headOrBase(headContents, baseContents, 'layers', {}) as Record<number, {id: number, name: string}>,
        externalDesignBlocks: this.loadedProjectData?.external_design_blocks ?? null,
        projectDocuments: this.loadedProjectData?.documents ?? null,
        projectPath: this.loadedProjectData?.project_path || null,
        variants: this.loadedProjectData?.variants ?? {},
        options: {
          replaceLayerIdsWithNames: true,
        },
      });
    },

    showInspectorForElement(targetEl: Element, svgEl: SVGSVGElement, baseContents: Record<string, any>, headContents: Record<string, any>, inspectorClickEvent: CustomEvent | null = null, prebuiltTree: TreeNode | null = null) {
        // if we are already loading a net we ignore the click
      if (this.isLoadingIndicatorEnabled) {
        return;
      }

      targetEl = targetEl.closest('[data-id]') ?? targetEl;
      const willShowNet = this.currentNet !== '' || targetEl.hasAttribute('data-net');

      let elementId = targetEl.getAttribute('data-id');
      if (elementId && !this.selectRegionEnabled()) {
          // Hide the inspector before loading/reloading.
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);

        this.setLoadingIndicator(true);

        let elemHeadContents = headContents;
        let elemBaseContents = baseContents;
        let type = '';

        // check if there is a path into the json
        if (targetEl.hasAttribute('data-path')) {
          let dataPath = targetEl.getAttribute('data-path').split('/');

          // if this primitive belongs to a component, show the entire component, not just the primitive
          if (dataPath.length > 1 && dataPath[0] === 'component_instances') {
            elementId = dataPath[1];
            dataPath = ['component_instances'];
            type = 'Component';
          } else {
            // ex: 'texts' -> 'Text'
            const group = dataPath[0].endsWith('s') ? dataPath[0].slice(0, -1) : dataPath[0];
            type = group.charAt(0).toUpperCase() + group.slice(1);
          }

          for (const segment of dataPath) {
            elemHeadContents = headOrBase(elemHeadContents, {}, segment, null);
            elemBaseContents = headOrBase(elemBaseContents, {}, segment, null);
          }

          const resolvedEntity = resolveInspectorEntity(
            elemHeadContents,
            elemBaseContents,
            elementId,
            dataPath,
            headOrBase,
            (this.inspectorDiffTreeState as InspectorDiffTreeState | undefined)?.getRawDiffJson(),
          );
          elemHeadContents = resolvedEntity.headEntity;
          elemBaseContents = resolvedEntity.baseEntity;
        } else {
          const resolvedEntity = resolveInspectorEntity(
            elemHeadContents,
            elemBaseContents,
            elementId,
            [],
            headOrBase,
            (this.inspectorDiffTreeState as InspectorDiffTreeState | undefined)?.getRawDiffJson(),
          );
          elemHeadContents = resolvedEntity.headEntity;
          elemBaseContents = resolvedEntity.baseEntity;
        }

        // Use the click's mouse position if there was one, else center on the element.
        const {x, y, width, height} = targetEl.getBoundingClientRect();
        const location = inspectorClickEvent === null ? {
          clientX: x + (width / 2),
          clientY: y + (height / 2),
        } : inspectorClickEvent.detail;

        const metadataCtx = this.buildInspectorMetadataContext(headContents, baseContents);

        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).setInspectorData(location,
          this.$refs.wrapper,
          elemHeadContents,
          elemBaseContents,
          headOrBase(elemHeadContents, elemBaseContents, 'type', type),
          targetEl.getAttribute('data-variant'),
          elementId,
          this.isPcb ? headOrBase(headContents, baseContents, 'units', 'mm') : '',
          metadataCtx,
          prebuiltTree,
        ).finally(() => {
          if (!willShowNet) {
            this.setLoadingIndicator(false);
          }
        });
      } else {
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
      }

      if (willShowNet) {
        this.setLoadingIndicator(true);

        // Toggle off any previous net first.
        // eslint-disable-next-line github/no-then
        this.toggleNet(svgEl, false, false).then(() => {
          const net = targetEl.getAttribute('data-net');
          if (net && !this.selectRegionEnabled()) {
            this.currentNet = net;
            return this.toggleNet(svgEl, true, false);
          }
        }).finally(() => {
          this.setLoadingIndicator(false);
        });
      }
    },

    hideInspector() {
      (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
    },

    async handleDollyZoomRequest(e: Event) {
      const {selector} = (e as CustomEvent<{selector: string}>).detail;
      const svg: SVGSVGElement = (this.$refs.headSvg as HTMLElement).querySelector('svg');

      // The event is global, so bail unless the selector matches this SVG.
      if (!svg?.querySelector(selector)) return;

      await this.dollyZoom(svg, selector);
    },

    removeGlobalEventHandlers() {
      document.body.removeEventListener('keydown', this.onKeyDown);
      document.body.removeEventListener('inspector-hide', this.handleinspectorHide);
      document.body.removeEventListener('dolly-zoom-request', this.handleDollyZoomRequest);
    },

    /**
     * @param {KeyboardEvent} e
     */
    onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        (this.$refs.ecadInspectorFrame as typeof EcadInspectorFrame).showInspector(false);
      }
    },

    /**
     * Close inspector window and unhighlight net
     */
    inspectorNetClose() {
      // Ignore while a net is loading.
      if (!this.isLoadingIndicatorEnabled && this.currentNet !== '') {
        this.setLoadingIndicator(true);

        const svg = (this.$refs.headSvg as HTMLElement).querySelector('svg') as SVGSVGElement;
        // Toggle off the selected net and close the popup.
        // eslint-disable-next-line github/no-then
        this.toggleNet(svg, false, true).then(() => {
          this.setLoadingIndicator(false);
        });
      }
    },

    setupPanZoom() {
      if (!this.isPanZoomEnabled || this.panZoom) return;

      const elem = this.$refs.panZoom as HTMLElement;
      const region = this.$refs.selectableRegion as HTMLElement;
      const headSvg = this.$refs.headSvg as HTMLElement | undefined;
      const svg = headSvg?.querySelector('svg') as SVGSVGElement | null;
      // The SVG may not be in the DOM yet; we'll be called again once it renders.
      if (!region || !svg || !headSvg) return;

      const handle = setupViewBoxPanZoom({
        elem,
        region,
        svg,
        inspectorTarget: headSvg,
        onScrollZoom: () => {
          this.panZoomHelpClassNames = 'hidden';
        },
      });
      this.panZoom = handle.controller;
      this.panZoomHandle = handle;
      this.panZoomHelpText = `${handle.keyModifier} + Scroll to zoom`;
    },

    setupSnippetReference(event?: Event) {
      const queryString = window.location.search;
      const queryParameters = new URLSearchParams(queryString);

      if (!queryParameters.has('view-coordinates')) return;

      const docId = queryParameters.get('doc-id');
      // Older links omit doc-id for single-page containers such as KiCad.
      // Accept those only for the document the container already selected;
      // multi-page links still need an exact id to avoid targeting every page.
      if (
        (docId === null && !this.initialSnippetClickthrough) ||
        (docId !== null && docId !== this.docId)
      ) {
        return;
      }
      // Reference element for the snippet reference component (snippets outside a DR).
      this.referenceElement = this.$el.querySelector('svg.schematic');

      // Opt-in zoom: when the link carries zoom=1, pan/zoom to frame the region
      // (not just highlight it). Existing links without the param keep the
      // highlight-only behaviour.
      if (queryParameters.get('zoom') === '1') {
        afterSnippetClickThrough(event, () => {
          fitViewCoordinates(queryParameters, this.referenceElement, this.panZoom);
        });
      }
    },

    /** Frame the elements matching `selector` (see dollyZoomToSelector). */
    async dollyZoom(svgEl: SVGSVGElement, selector: string) {
      if (this.panZoom) await dollyZoomToSelector(this.panZoom, svgEl, selector);
    },

    allDiffLoaded() {
      this.checkIfAllLoaded();
    },

    onDiffFromLoad() {
      this.isDiffFromLoaded = true;
      if (this.isDiffToLoaded) {
        this.allDiffLoaded();
      }
    },

    onDiffToLoad() {
      this.isDiffToLoaded = true;
      if (this.isDiffFromLoaded) {
        this.allDiffLoaded();
      }
    },

    onDiffError(fetchRetryer: FetchRetryer) {
      this.isDiffLoadError = true;
      if (fetchRetryer.response) {
        this.displayDiffResponseError(fetchRetryer.response);
      } else {
        this.displayUnknownError(fetchRetryer.errorDetails);
      }
    },

    /**
     * @private
     * @return {Element | null}
     */
    findASLoading() {
      const container = this.$el.closest('.diff-file-box') ?? this.$el.closest('.non-diff-file-content');
      if (!container) return null;
      return container.querySelector('.allspice-loading');
    },
  },
});

</script>

<template>
  <div
    ref="wrapper"
    class="svg-wrapper"
    :class="{'hide-nonvisual-diff-overlay-pending': hideNonVisualDiffOverlayUntilReady}"
  >
    <ecad-inspector-frame
      ref="ecadInspectorFrame"
      :is-diff="isDiff"
      @inspector-did-close="inspectorNetClose"
    />
    <div class="svg-schematic">
      <div
        :class="svgContainerClassNames"
        class="selectable-region-parent"
      >
        <div
          ref="selectableRegion"
          class="selectable-region"
          :data-doc-id="docId"
        >
          <div
            ref="panZoomContainer"
            class="pan-zoom-container"
          >
            <div
              ref="panZoom"
              class="pan-zoom"
            >
              <div
                :class="svgClassNames"
                :data-as-view-coords="dataAsViewCoords"
                :data-as-aspect-ratio="dataAsAspectRatio"
              >
                <!-- eslint-disable vue/no-v-html -->
                <div
                  ref="headSvg"
                  v-html="headSvg"
                />
                <!-- eslint-enable vue/no-v-html -->

                <allspice-snippet-reference
                  v-if="!isLoading && referenceElement"
                  :key="(referenceElement as any)"
                  :reference-element="referenceElement"
                  :pan-zoom="panZoom"
                  :is-active="initialSnippetClickthrough"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isLoading && isLoadError && displayDefaultOnError">
        <img
          class="ui active centered inline text"
          :src="failedImageUrl"
        >
      </div>
    </div>
    <div
      v-if="isPanZoomEnabled"
      class="pan-zoom-help-text"
      :class="panZoomHelpClassNames"
    >
      {{ panZoomHelpText }}
    </div>
    <div
      v-if="showErrorDetails"
      class="ui negative message"
    >
      {{ errorDetailsText }}
    </div>
  </div>
</template>

<!-- eslint-disable vue-scoped-css/no-unused-selector -->
<style scoped>

.svg-wrapper {
  margin: 4px;
  position: relative;
  @media (max-width: 991px) {
    margin: 4px 25px 4px 4px;
  }
}

.svg-schematic {
  box-sizing: border-box;
  border: solid 1px #495057;
  width: 100%;
  max-height: 100%;
}

.svg-schematic svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* SVG is a viewport filling .selectable-region; zoom via viewBox, so no CSS transform. Scoped so AllSpiceDiffView is untouched. */
.svg-schematic .selectable-region {
  position: relative;
  /* Drag to pan must not start native text selection; also suppresses WebKit's I-beam over SVG <text>. */
  cursor: default;
  user-select: none;
}

.svg-schematic .pan-zoom-container {
  position: absolute;
  inset: 0;
}

/* Fill the container so select-region's offset/box coordinates are in region space. */
.svg-schematic .pan-zoom {
  position: absolute;
  inset: 0;
}

.svg-schematic svg text.hover {
  stroke-width: 0.5px;
  stroke: currentColor;
}

.svg-schematic svg .hover text {
  stroke-width: 0.5px;
  stroke: currentColor;
}

.hide-nonvisual-diff-overlay-pending :deep(.diff.diff_from),
.hide-nonvisual-diff-overlay-pending :deep(.diff.diff_to),
.hide-nonvisual-diff-overlay-pending :deep(.diff-backdrop) {
  visibility: hidden;
}

.pan-zoom-help-text {
  opacity: 0.7;
  text-align: center;
  min-height: 1lh;
}

.pan-zoom-help-text.hidden {
  visibility: hidden;
}

</style>
<!-- eslint-disable -->
